/**
 * click-event.js — Kenaz GTM / GA4 click event tracking
 *
 * Requires analytics.js to be loaded first (provides event functions).
 *
 * How it works:
 *   A single delegated listener on document catches clicks on any element
 *   that has a [data-track] attribute. Routes to appropriate event function.
 *
 *   Add to any clickable element in your Liquid templates:
 *     data-track="<event_name>"
 *     data-params='{ "key": "value" }'
 *
 * Event Routing:
 *  - navigation_menu: cart_icon_clicked, search_icon_clicked, hamburger_clicked
 *  - share_event: share_clicked
 *  - review_event: reviews_clicked
 *  - qty_event: qty_plus_clicked, qty_minus_clicked
 *  - byob_event: add_to_box_clicked, buy_now_clicked, byob_banner_clicked, byob_product_clicked
 *  - click_event: all others (fallback)
 */

(function () {
  'use strict';

  // Event routing map
  var EVENT_ROUTING = {
    // Navigation menu events
    cart_icon_clicked:   'navigation_menu',
    search_icon_clicked: 'navigation_menu',
    hamburger_clicked:   'navigation_menu',

    // Share events
    share_clicked: 'share_event',

    // Review events
    reviews_clicked: 'review_event',

    // Quantity events
    qty_plus_clicked:  'qty_event',
    qty_minus_clicked: 'qty_event',

    // BYOB events
    add_to_box_clicked:    'byob_event',
    buy_now_clicked:       'byob_event',
    byob_banner_clicked:   'byob_event',
    byob_product_clicked:  'byob_event'
  };

  function init() {
    // Guard: analytics utility must be ready
    if (typeof window.navigation_menu !== 'function') {
      console.warn('[Kenaz] click-event.js loaded before analytics.js — events will not fire.');
      return;
    }

    /* ─── Global delegated click listener ─────────────────────────
     *  Uses CAPTURE phase (3rd arg = true) so the listener fires
     *  top-down, before any child handler can call stopPropagation().
     *  This ensures login modals / drawer JS that intercept clicks
     *  don't swallow the event before we can read [data-track].
     * ─────────────────────────────────────────────────────────── */
    document.addEventListener('click', function (e) {
      // Ignore programmatic .click() calls (e.g. internal drawer close logic).
      if (!e.isTrusted) return;

      var el = e.target.closest('[data-track]');
      if (!el) return;

      var eventName = el.dataset.track;
      var params    = {};

      try {
        params = JSON.parse(el.dataset.params || '{}');
      } catch (err) {
        console.warn('[Kenaz] click-event.js — invalid JSON in data-params on:', el, err);
      }

      /* ─── Special case: hamburger summary toggle ───────────────────
       *  details.open is state BEFORE toggle:
       *    false → user is opening  → action = "open"
       *    true  → user is closing  → action = "close"
       * ─────────────────────────────────────────────────────────── */
      if (eventName === 'hamburger_clicked' && el.tagName === 'SUMMARY') {
        var details = el.closest('details');
        params.action            = details && details.open ? 'close' : 'open';
        params.item_clicked_name = null;
        params.url               = null;
      }

      /* ─── Special case: add_to_box_clicked label toggle ────────────
       *  The label wraps a checkbox. At click time checkbox.checked is
       *  state BEFORE toggle:
       *    checked === false → user is adding   → action = "add"
       *    checked === true  → user is removing → action = "remove"
       * ─────────────────────────────────────────────────────────── */
      if (eventName === 'add_to_box_clicked' && el.tagName === 'LABEL') {
        var checkbox = el.querySelector('input[type="checkbox"]');
        params.action = (checkbox && checkbox.checked) ? 'remove' : 'add';
      }

      /* ─── Special case: qty buttons ────────────────────────────────
       *  Detect plus/minus action from button class or data attribute
       * ─────────────────────────────────────────────────────────── */
      if (eventName === 'qty_plus_clicked') {
        params.action = 'increase';
      }
      if (eventName === 'qty_minus_clicked') {
        params.action = 'decrease';
      }

      /* ─── Route to appropriate event function ─────────────────── */
      var eventType = EVENT_ROUTING[eventName];

      if (eventType === 'navigation_menu') {
        window.navigation_menu(eventName, params);
      } else if (eventType === 'share_event') {
        window.share_event(params);
      } else if (eventType === 'review_event') {
        window.review_event(params);
      } else if (eventType === 'qty_event') {
        window.qty_event(params);
      } else if (eventType === 'byob_event') {
        window.byob_event(eventName, params);
      } else {
        // Fallback to generic click_event
        window.click_event(eventName, params);
      }

    }, true); // capture phase

    console.log('[Kenaz] click-event.js — delegated listener active.');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
